// Match shop components on the tank (Design §7.2): cannon, armor and drive change the shape, never
// the colour, so they fit every paint and the player colour stays; the ammo shows only on the
// bullets. Tank space: x = forward.

/** Indices into `sim::Loadout::to_bytes` and the parts in wire order (see `shop.ts` SLOTS). */
const [CANNON, ARMOR, DRIVE] = [0, 1, 2];
const [HEAVY_GUN, RAPID, PRECISION, SCATTER, DRUM, CHARGED, TWIN] = [1, 2, 3, 4, 5, 6, 7];
const [HEAVY, LIGHT, REACTIVE, FRONT, RAM] = [1, 2, 3, 4, 5];
const [SPORT, TRACKS_PLUS, HOVER] = [1, 2, 3];

/** Components of one tank: cannon, armor, drive, ammo; missing = standard. */
export type Parts = ArrayLike<number>;
export const STANDARD_PARTS: Parts = [0, 0, 0, 0];

const BARREL = '#222';
const STEEL = '#9aa4ad';
/** Reactive tiles on the hull, centre x, y in tank radii: the front layer, then the rear one. */
export const REACTIVE_TILES = [[0.55, -0.27], [0.55, 0.27], [-0.55, -0.27], [-0.55, 0.27]];
/** Tiles per layer; a hit blows off the front layer first. */
export const TILES_PER_LAYER = 2;
const TILE = 0.26;

/** Adds the tracks (or the hover skirt) as subpaths. */
export function tracksPath(ctx: CanvasRenderingContext2D, p: Parts, r: number): void {
  if (p[DRIVE] === HOVER) ctx.roundRect(-r, -r * 0.8, r * 2, r * 1.6, r * 0.35);
  else if (p[DRIVE] === TRACKS_PLUS) {
    ctx.rect(-r, -r * 0.95, r * 2, r * 0.52);
    ctx.rect(-r, r * 0.43, r * 2, r * 0.52);
  } else {
    ctx.rect(-r, -r * 0.85, r * 2, r * 0.4);
    ctx.rect(-r, r * 0.45, r * 2, r * 0.4);
  }
}

/** Adds the hull as subpaths: light with bevelled corners, heavy with skirts over the tracks (unless `skirts` is false), front with a wedge. */
export function hullPath(ctx: CanvasRenderingContext2D, p: Parts, r: number, skirts = true): void {
  const [w, h] = [r * 0.85, r * 0.5];
  if (p[ARMOR] === LIGHT) {
    const c = r * 0.22;
    ctx.moveTo(-w + c, -h);
    ctx.lineTo(w - c, -h);
    ctx.lineTo(w, -h + c);
    ctx.lineTo(w, h - c);
    ctx.lineTo(w - c, h);
    ctx.lineTo(-w + c, h);
    ctx.lineTo(-w, h - c);
    ctx.lineTo(-w, -h + c);
    ctx.closePath();
  } else ctx.rect(-w, -h, w * 2, h * 2);
  if (skirts) skirtsPath(ctx, p, r);
  if (p[ARMOR] === FRONT) frontPlate(ctx, r);
}

/** Adds heavy armor's skirts over the tracks as subpaths (none for the other armors). */
export function skirtsPath(ctx: CanvasRenderingContext2D, p: Parts, r: number): void {
  if (p[ARMOR] !== HEAVY) return;
  ctx.rect(-r * 0.8, -r * 0.9, r * 1.6, r * 0.42);
  ctx.rect(-r * 0.8, r * 0.48, r * 1.6, r * 0.42);
}

function frontPlate(ctx: CanvasRenderingContext2D, r: number): void {
  ctx.moveTo(r * 0.6, -r * 0.5);
  ctx.lineTo(r * 0.88, -r * 0.5);
  ctx.lineTo(r * 1.04, 0);
  ctx.lineTo(r * 0.88, r * 0.5);
  ctx.lineTo(r * 0.6, r * 0.5);
  ctx.closePath();
}

/** Hull outline width: heavy thicker, light thinner. */
export function hullLine(p: Parts): number {
  return p[ARMOR] === HEAVY ? 3 : p[ARMOR] === LIGHT ? 1.5 : 2;
}

/** Under the tank: the hover glow, pulsing. */
export function drawUnder(ctx: CanvasRenderingContext2D, p: Parts, r: number, now: number): void {
  if (p[DRIVE] !== HOVER) return;
  const size = r * (1.3 + 0.06 * Math.sin(now / 160));
  const glow = ctx.createRadialGradient(0, 0, r * 0.5, 0, 0, size);
  glow.addColorStop(0, 'rgba(90, 200, 255, 0.7)');
  glow.addColorStop(1, 'rgba(90, 200, 255, 0)');
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.arc(0, 0, size, 0, Math.PI * 2);
  ctx.fill();
}

export function hovers(p: Parts): boolean {
  return p[DRIVE] === HOVER;
}

/** Muzzles of the cannon: how far forward of the tank origin, and their sideways offsets. */
export function muzzles(p: Parts, l: number): [number, number[]] {
  switch (p[CANNON]) {
    case PRECISION:
      return [l * 1.35, [0]];
    case SCATTER:
      return [l * 0.92, [0]];
    case HEAVY_GUN:
      return [l + 1, [0]];
    case TWIN:
      return [l, [-3.8, 3.8]];
    default:
      return [l, [0]];
  }
}

/** Hover tanks bob a little: their scale now. */
export function bob(p: Parts, now: number): number {
  return hovers(p) ? 1 + 0.03 * Math.sin(now / 220) : 1;
}

/**
 * Draws `body` (in tank space, x = forward) as it rocks: tracks start and stop at once, so the
 * hull tips instead, `tilt` −1 (nose up, driving off) .. 1 (nose down, stopping). Seen from
 * above it shifts that way and looks a little shorter, and the end coming up is wider, as if
 * nearer: then it goes through an offscreen canvas drawn in strips of growing height.
 */
export function rock(ctx: CanvasRenderingContext2D, p: Parts, r: number, tilt: number, body: (g: CanvasRenderingContext2D) => void): void {
  if (p[DRIVE] !== TRACKS_PLUS || tilt === 0) return body(ctx);
  ctx.translate(tilt * r * ROCK_SHIFT, 0);
  ctx.scale(1 - Math.abs(tilt) * ROCK_SHORTEN, 1);
  if (Math.abs(tilt) < ROCK_FLAT) return body(ctx);
  const m = ctx.getTransform();
  const px = Math.hypot(m.a, m.b); // canvas pixels per unit, so the copy is as sharp
  const half = r * ROCK_REACH;
  const size = Math.ceil(2 * half * px);
  lifted ??= document.createElement('canvas');
  if (lifted.width < size) lifted.width = lifted.height = size;
  const g = lifted.getContext('2d')!;
  g.setTransform(1, 0, 0, 1, 0, 0);
  g.clearRect(0, 0, size, size);
  g.setTransform(px, 0, 0, px, size / 2, size / 2);
  body(g);
  const step = (2 * half) / ROCK_STRIPS;
  for (let k = 0; k < ROCK_STRIPS; k++) {
    const x0 = -half + k * step;
    // Wider towards the end that comes up: the rear when stopping, the front when driving off.
    const wide = 1 - ROCK_WIDEN * tilt * ((x0 + step / 2) / half);
    const h = 2 * half * wide;
    // A hair of overlap, so no seams show between the strips.
    ctx.drawImage(lifted, (x0 + half) * px, 0, step * px, 2 * half * px, x0, -h / 2, step + 0.05, h);
  }
}

/** Rocking: shift (tank radii) and shortening at full tilt, the widening of the end coming up,
 * below which tilt it is drawn flat, how far the tank reaches (radii, barrel included) and the
 * strips of the lifted copy. */
const ROCK_SHIFT = 0.14;
const ROCK_SHORTEN = 0.08;
const ROCK_WIDEN = 0.14;
const ROCK_FLAT = 0.02;
const ROCK_REACH = 2.6;
const ROCK_STRIPS = 20;
let lifted: HTMLCanvasElement | undefined;

/** Details on top of tracks and hull: track links, steel on heavy and front armor, reactive tiles, spoiler. */
export function drawDetails(ctx: CanvasRenderingContext2D, p: Parts, r: number, reactiveLayers: number): void {
  ctx.save();
  if (p[ARMOR] === RAM) {
    // Ram: a steel beam across the bow with three spikes.
    ctx.fillStyle = '#8a9096';
    ctx.strokeStyle = '#111';
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.rect(r * 0.82, -r * 0.82, r * 0.22, r * 1.64);
    for (const y of [-0.55, 0, 0.55]) {
      ctx.moveTo(r * 1.04, (y - 0.16) * r);
      ctx.lineTo(r * 1.36, y * r);
      ctx.lineTo(r * 1.04, (y + 0.16) * r);
    }
    ctx.fill();
    ctx.stroke();
  }
  if (p[DRIVE] === TRACKS_PLUS) {
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.45)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let x = -r + r * 0.2; x < r; x += r * 0.25) {
      ctx.moveTo(x, -r * 0.95);
      ctx.lineTo(x, -r * 0.43);
      ctx.moveTo(x, r * 0.43);
      ctx.lineTo(x, r * 0.95);
    }
    ctx.stroke();
  }
  if (p[DRIVE] === SPORT) {
    // Spoiler behind the tracks, two exhausts at the rear of the hull.
    ctx.fillStyle = BARREL;
    ctx.fillRect(-r * 1.14, -r * 0.72, r * 0.14, r * 1.44);
    ctx.fillStyle = '#666';
    for (const y of [-0.25, 0.25]) {
      ctx.beginPath();
      ctx.arc(-r * 0.74, y * r, r * 0.08, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  if (p[ARMOR] === FRONT || p[ARMOR] === HEAVY) {
    ctx.fillStyle = 'rgba(0, 0, 0, 0.22)';
    ctx.beginPath();
    if (p[ARMOR] === FRONT) frontPlate(ctx, r);
    else {
      ctx.rect(-r * 0.8, -r * 0.9, r * 1.6, r * 0.42);
      ctx.rect(-r * 0.8, r * 0.48, r * 1.6, r * 0.42);
    }
    ctx.fill();
  }
  if (p[ARMOR] === REACTIVE && reactiveLayers > 0) {
    ctx.fillStyle = STEEL;
    ctx.strokeStyle = '#111';
    ctx.lineWidth = 1;
    for (const [x, y] of REACTIVE_TILES.slice(-reactiveLayers * TILES_PER_LAYER)) drawTile(ctx, x * r, y * r, r);
  }
  ctx.restore();
}

/** One reactive tile centred on x, y (fill and stroke style set by the caller). */
export function drawTile(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  const s = TILE * r;
  ctx.fillRect(x - s / 2, y - s / 2, s, s);
  ctx.strokeRect(x - s / 2, y - s / 2, s, s);
}

/**
 * Turret and barrel at the tank origin; the turret is stroked in the caller's style.
 * `charge` 0..1 lights the coils of the charge cannon.
 */
export function drawTurret(
  ctx: CanvasRenderingContext2D,
  r: number,
  length: number,
  fill: string | CanvasPattern,
  p: Parts = STANDARD_PARTS,
  charge = 0,
  heat = 0,
  pulse = 0,
): void {
  if (p[CANNON] === DRUM) {
    // Drum with six chambers behind the turret.
    ctx.beginPath();
    ctx.arc(-r * 0.42, 0, r * 0.3, 0, Math.PI * 2);
    ctx.fillStyle = '#333';
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = '#777';
    for (let k = 0; k < 6; k++) {
      const a = (k * Math.PI) / 3;
      ctx.beginPath();
      ctx.arc(-r * 0.42 + Math.cos(a) * r * 0.17, Math.sin(a) * r * 0.17, r * 0.055, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  drawBarrel(ctx, p[CANNON], length, charge, heat, pulse);
  ctx.beginPath();
  ctx.arc(0, 0, r * 0.4, 0, Math.PI * 2);
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.stroke();
  if (p[CANNON] === PRECISION) {
    // Scope on the turret.
    ctx.fillStyle = '#333';
    ctx.fillRect(-r * 0.15, -r * 0.33, r * 0.55, r * 0.13);
  }
}

/**
 * Barrel(s) for `cannon`; an overheating charge turns from blue to red by `heat` 0..1, then
 * pulses by `pulse` 0..1.
 */
function drawBarrel(ctx: CanvasRenderingContext2D, cannon: number, l: number, charge: number, heat: number, pulse: number): void {
  ctx.fillStyle = BARREL;
  switch (cannon) {
    case HEAVY_GUN:
      ctx.fillRect(0, -3.8, l, 7.6);
      ctx.fillRect(l - 4, -5.2, 5, 10.4); // muzzle brake
      break;
    case RAPID:
      ctx.fillRect(0, -1.8, l, 3.6);
      for (const k of [0.45, 0.6, 0.75]) ctx.fillRect(l * k - 1, -3.2, 2, 6.4); // cooling ribs
      break;
    case PRECISION:
      ctx.fillRect(0, -1.8, l * 1.35, 3.6);
      break;
    case SCATTER: {
      // Short barrel flaring out like a funnel.
      ctx.fillRect(0, -2.5, l * 0.62, 5);
      ctx.beginPath();
      ctx.moveTo(l * 0.6, -2.5);
      ctx.lineTo(l * 0.92, -5.5);
      ctx.lineTo(l * 0.92, 5.5);
      ctx.lineTo(l * 0.6, 2.5);
      ctx.closePath();
      ctx.fill();
      break;
    }
    case CHARGED:
      ctx.fillRect(0, -2.5, l, 5);
      for (const k of [0.35, 0.55, 0.75]) {
        ctx.fillStyle = '#b87333'; // copper coils, glowing while charging
        ctx.fillRect(l * k - 1.2, -3.8, 2.4, 7.6);
        if (charge > 0) {
          const mix = (blue: number, red: number) => Math.round(blue + (red - blue) * heat);
          ctx.fillStyle = `rgba(${mix(90, 255)}, ${mix(220, 140 * (1 - pulse))}, ${mix(255, 40)}, ${charge})`;
          ctx.fillRect(l * k - 1.2, -3.8, 2.4, 7.6);
        }
      }
      if (heat > 0) {
        ctx.fillStyle = `rgba(230, 25, 25, ${(0.35 + 0.5 * pulse) * heat})`;
        ctx.fillRect(0, -2.5, l, 5);
      }
      break;
    case TWIN:
      ctx.fillRect(0, -5.6, l, 3.6);
      ctx.fillRect(0, 2, l, 3.6);
      break;
    default:
      ctx.fillRect(0, -2.5, l, 5);
  }
}
